import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Grid,
} from "@mui/material";
import registrationService from "../../services/registrationService";

const steps = ["PRN Lookup", "Department & Class", "Details & Submit"];
const EDIT_SESSION_DURATION = 30 * 60; // 30 minutes in seconds

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const COUNTRIES = ["India", "Nepal", "Bangladesh", "Sri Lanka", "Other"];

const RegistrationPortal = () => {
  // Step management
  const [activeStep, setActiveStep] = useState(0);

  // Step 1 state
  const [prn, setPrn] = useState("");
  const [lookupMode, setLookupMode] = useState("prn"); // "prn" or "email"
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupEmailForOtp, setLookupEmailForOtp] = useState("");
  const [instituteId, setInstituteId] = useState(null);
  const [instituteName, setInstituteName] = useState("");

  // Pre-assigned data from PRN catalog (skip Step 2 when present)
  const [preAssigned, setPreAssigned] = useState(null);

  // Step 2 state
  const [departments, setDepartments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [departmentId, setDepartmentId] = useState("");
  const [classId, setClassId] = useState("");

  // Step 3 state — Personal Information
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [rollNumber, setRollNumber] = useState("");

  // Step 3 state — Parent/Guardian Information
  const [parentName, setParentName] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const [parentWhatsapp, setParentWhatsapp] = useState("");
  const [parentEmail, setParentEmail] = useState("");

  // Step 3 state — Address Information
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [addressState, setAddressState] = useState("");
  const [country, setCountry] = useState("India");
  const [pinCode, setPinCode] = useState("");

  // Form validation errors (inline display)
  const [formErrors, setFormErrors] = useState({});

  // Shared state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // View state: "register" (default) | "otp" | "edit"
  const [view, setView] = useState("register");

  // OTP flow state
  const [otpPrn, setOtpPrn] = useState(""); // PRN used for OTP flow
  const [otp, setOtp] = useState("");
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [otpPhase, setOtpPhase] = useState("request"); // "request" | "verify"

  // Edit session state
  const [editToken, setEditToken] = useState("");
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Edit record state
  const [record, setRecord] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [editSuccess, setEditSuccess] = useState("");

  // Refs for timers
  const cooldownTimer = useRef(null);
  const sessionTimer = useRef(null);

  // Fetch departments when step 2 is reached and instituteId or PRN is available
  useEffect(() => {
    if (activeStep === 1 && (instituteId || prn.trim())) {
      fetchDepartments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, instituteId]);

  // Fetch classes when department changes
  useEffect(() => {
    if (departmentId && (instituteId || prn.trim())) {
      fetchClasses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId]);

  // Pre-fill rollNumber from preAssigned data
  useEffect(() => {
    if (preAssigned) {
      if (preAssigned.rollNumber) setRollNumber(preAssigned.rollNumber);
    }
  }, [preAssigned]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
      if (sessionTimer.current) clearInterval(sessionTimer.current);
    };
  }, []);

  // Session countdown timer
  useEffect(() => {
    if (!sessionExpiresAt) return;

    const updateCountdown = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((sessionExpiresAt - now) / 1000));
      setTimeRemaining(remaining);

      if (remaining <= 0) {
        setSessionExpired(true);
        clearInstituteState();
        setView("otp");
        setOtpPhase("request");
        setEditToken("");
        setRecord(null);
        if (sessionTimer.current) clearInterval(sessionTimer.current);
      }
    };

    updateCountdown();
    sessionTimer.current = setInterval(updateCountdown, 1000);

    return () => {
      if (sessionTimer.current) clearInterval(sessionTimer.current);
    };
  }, [sessionExpiresAt]);

  // OTP cooldown timer
  useEffect(() => {
    if (otpCooldown <= 0) {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
      return;
    }

    cooldownTimer.current = setInterval(() => {
      setOtpCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownTimer.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    };
  }, [otpCooldown]);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const params = instituteId
        ? { instituteId }
        : { prn: prn.trim() };
      const res = await registrationService.getDepartments(params);
      setDepartments(res.data?.data || res.data || []);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load departments."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const params = instituteId
        ? { instituteId, departmentId }
        : { prn: prn.trim(), departmentId };
      const res = await registrationService.getClasses(params);
      setClasses(res.data?.data || res.data || []);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load classes."
      );
    } finally {
      setLoading(false);
    }
  };

  // --- State cleanup helpers ---
  const clearInstituteState = () => {
    setInstituteId(null);
    setInstituteName("");
  };

  const clearOtpState = () => {
    setOtpPrn("");
    setOtp("");
    setOtpPhase("request");
    setOtpCooldown(0);
  };

  const clearSessionState = () => {
    setEditToken("");
    setRecord(null);
    setSessionExpiresAt(null);
    setTimeRemaining(null);
    setSessionExpired(false);
  };

  // Step 1: PRN Lookup
  const handlePrnLookup = async () => {
    setError("");
    if (!prn.trim()) {
      setError("Please enter a PRN.");
      return;
    }

    try {
      setLoading(true);
      const res = await registrationService.lookupPrn(prn.trim());
      const data = res.data?.data || res.data;
      const action = data?.action;

      if (action === "register") {
        // Fetch availability using PRN to obtain instituteId
        const availRes = await registrationService.getAvailability({ prn: prn.trim() });
        const availability = availRes.data?.data || availRes.data;

        if (!availability?.available) {
          setError(
            "Registration is closed, please contact the institute admin."
          );
          return;
        }

        // Store instituteId from availability response
        setInstituteId(availability.instituteId);

        // Check for pre-assigned department/class data
        if (availability.preAssigned && availability.preAssigned.departmentId && availability.preAssigned.classId) {
          // Store pre-assigned data and skip Step 2
          setPreAssigned(availability.preAssigned);
          setDepartmentId(availability.preAssigned.departmentId);
          setClassId(availability.preAssigned.classId);
          setActiveStep(2); // Skip to Step 3 directly
        } else {
          // No pre-assignment — show Step 2 (manual selection)
          setPreAssigned(null);
          setActiveStep(1);
        }
      } else if (action === "verify") {
        // OTP was auto-sent by backend during lookup
        setOtpPrn(prn.trim());
        clearInstituteState();
        clearSessionState();
        setView("otp");
        setOtpPhase("verify");
      } else {
        // Unrecognized action
        setError("Unable to complete lookup. Please try again.");
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setError(err.response?.data?.message || "Invalid PRN format. Please check and try again.");
      } else if (status === 429) {
        setError("Too many requests. Please try again later.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 1 (email mode): Email Lookup
  const validateEmailFormat = (emailValue) => {
    if (!emailValue || emailValue.length > 254) return false;
    const atIndex = emailValue.indexOf("@");
    if (atIndex < 1) return false;
    const dotAfterAt = emailValue.indexOf(".", atIndex + 1);
    if (dotAfterAt < atIndex + 2) return false;
    return true;
  };

  const handleEmailLookup = async () => {
    setError("");
    const trimmedEmail = lookupEmail.trim();

    if (!trimmedEmail) {
      setError("Please enter an email address.");
      return;
    }

    if (!validateEmailFormat(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);
      const res = await registrationService.lookupByEmail(trimmedEmail);
      const data = res.data?.data || res.data;

      if (data?.action === "verify" && data?.otpSent === true) {
        // Store email for OTP verification (no PRN available from response)
        setLookupEmailForOtp(trimmedEmail);
        clearInstituteState();
        clearSessionState();
        setView("otp");
        setOtpPhase("verify");
      } else {
        // otpSent is false or missing
        setError("OTP could not be sent. Please try again.");
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setError(err.response?.data?.message || "Invalid email format. Please check and try again.");
      } else if (status === 429) {
        setError("Too many requests. Please try again later.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Department & Class selection
  const handleDepartmentChange = (e) => {
    const newDeptId = e.target.value;
    setDepartmentId(newDeptId);
    // Clear class on department change (Req 5.7)
    setClassId("");
    setClasses([]);
  };

  const handleClassChange = (e) => {
    const newClassId = e.target.value;
    setClassId(newClassId);
  };

  const handleStep2Next = () => {
    setError("");
    if (!departmentId) {
      setError("Please select a department.");
      return;
    }
    if (!classId) {
      setError("Please select a class.");
      return;
    }
    setActiveStep(2);
  };

  // Step 3: Form validation
  const validateForm = () => {
    const errors = {};

    // firstName: required, 1–100 chars
    if (!firstName.trim()) errors.firstName = "First name is required";
    else if (firstName.trim().length > 100) errors.firstName = "Max 100 characters";

    // email: required, valid format, max 254
    if (!email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errors.email = "Invalid email format";
    else if (email.trim().length > 254) errors.email = "Max 254 characters";

    // phone: required, 7–15 digits with optional leading +
    if (!phone.trim()) errors.phone = "Phone is required";
    else if (!/^\+?[0-9]{7,15}$/.test(phone.trim())) errors.phone = "Invalid phone (7-15 digits, optional leading +)";

    // dob: optional, if provided must match YYYY-MM-DD
    if (dob && !/^\d{4}-\d{2}-\d{2}$/.test(dob))
      errors.dob = "Invalid date format (YYYY-MM-DD)";

    // parentPhone: optional, if provided must match phone pattern
    if (parentPhone.trim() && !/^\+?[0-9]{7,15}$/.test(parentPhone.trim()))
      errors.parentPhone = "Invalid phone format (7-15 digits, optional leading +)";

    // parentWhatsapp: optional, must start with + followed by country code
    if (parentWhatsapp.trim() && !/^\+[1-9][0-9]{6,14}$/.test(parentWhatsapp.trim()))
      errors.parentWhatsapp = "Must start with + followed by country code and number";

    // parentEmail: optional, if provided must be valid email
    if (parentEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parentEmail.trim()))
      errors.parentEmail = "Invalid email format";
    else if (parentEmail.trim().length > 254)
      errors.parentEmail = "Max 254 characters";

    // pinCode: optional, if provided must be exactly 6 digits
    if (pinCode.trim() && !/^[0-9]{6}$/.test(pinCode.trim()))
      errors.pinCode = "Must be exactly 6 digits";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Step 3: Submit
  const handleSubmit = async () => {
    setError("");

    if (!validateForm()) return;

    try {
      setLoading(true);

      // Build formData from all expanded fields
      const formData = {
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        phone: phone.trim(),
        dob: dob || undefined,
        gender: gender || undefined,
        rollNumber: rollNumber.trim() || undefined,
        parentName: parentName.trim() || undefined,
        parentPhone: parentPhone.trim() || undefined,
        parentWhatsapp: parentWhatsapp.trim() || undefined,
        parentEmail: parentEmail.trim() || undefined,
        street: street.trim() || undefined,
        city: city.trim() || undefined,
        state: addressState || undefined,
        country: country || undefined,
        pinCode: pinCode.trim() || undefined,
      };

      // Remove undefined values before submission
      Object.keys(formData).forEach(
        (k) => formData[k] === undefined && delete formData[k]
      );

      await registrationService.submit({
        prn: prn.trim(),
        email: email.trim(),
        departmentId,
        classId,
        formData,
      });
      setSuccess(true);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Submission failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // --- OTP / Edit Flow Handlers ---

  // Format time remaining as MM:SS
  const formatTime = (seconds) => {
    if (seconds == null) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  // Extract per-field errors from server response
  const extractFieldErrors = (responseData) => {
    const errorsObj = responseData?.errors || responseData?.data?.errors;
    if (errorsObj && typeof errorsObj === "object") {
      const serverFieldErrors = {};
      Object.entries(errorsObj).forEach(([field, msg]) => {
        serverFieldErrors[field] = Array.isArray(msg) ? msg[0] : msg;
      });
      return serverFieldErrors;
    }
    return null;
  };

  // Request OTP
  const handleRequestOtp = async () => {
    setError("");
    setEditSuccess("");

    if (!otpPrn.trim()) {
      setError("Please enter a PRN.");
      return;
    }

    try {
      setLoading(true);
      await registrationService.requestOtp(otpPrn.trim());
      setEditSuccess("OTP sent to your registered email. Valid for 10 minutes.");
      setOtpPhase("verify");
      setOtpCooldown(60);
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error;
      if (err.response?.status === 429 || message?.toLowerCase().includes("rate")) {
        setError("Maximum OTP requests reached. Please try again later.");
      } else {
        setError(message || "Failed to send OTP. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    setError("");
    setEditSuccess("");

    if (otp.trim().length !== 6) {
      setError("Please enter a valid 6-digit OTP.");
      return;
    }

    try {
      setLoading(true);
      const res = await registrationService.verifyOtp({ prn: otpPrn.trim(), otp: otp.trim() });
      const data = res.data?.data || res.data;

      // Store token and set up session
      const token = data.token || data.editSessionToken;
      setEditToken(token);

      // Use expiresAt from response (ISO 8601) for session expiry calculation
      const expiresAt = data.expiresAt
        ? new Date(data.expiresAt).getTime()
        : data.editSessionExpiresAt
          ? new Date(data.editSessionExpiresAt).getTime()
          : Date.now() + EDIT_SESSION_DURATION * 1000;
      setSessionExpiresAt(expiresAt);
      setSessionExpired(false);

      // Extract and store enriched institute info from OTP verify response
      setInstituteId(data.instituteId || null);
      setInstituteName(data.instituteName || "");
      // Response PRN is authoritative — overwrite otpPrn
      setOtpPrn(data.prn || otpPrn.trim());

      // Block navigation if instituteId is missing
      if (!data.instituteId) {
        setError("Institute information could not be retrieved.");
        return;
      }

      // Fetch the current record
      await fetchRecord(token);
      setView("edit");
      setEditSuccess("OTP verified. You can now edit your registration.");
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error;
      if (message?.toLowerCase().includes("expired")) {
        setError("OTP has expired. Please request a new one.");
      } else if (message?.toLowerCase().includes("attempts")) {
        setError("Too many failed attempts. Please request a new OTP.");
      } else if (message?.toLowerCase().includes("incorrect") || message?.toLowerCase().includes("invalid")) {
        setError("Incorrect OTP. Please check and try again.");
      } else {
        setError(message || "OTP verification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch record using edit session token
  const fetchRecord = async (token) => {
    try {
      const res = await registrationService.getRecord({ token });
      const data = res.data?.data || res.data;
      setRecord(data);
      setEditFields({
        email: data.email || "",
        fullName: data.formData?.fullName || data.fullName || "",
        phone: data.formData?.phone || data.phone || "",
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load your record.");
    }
  };

  // Handle edit field changes
  const handleEditFieldChange = (field) => (e) => {
    setEditFields((prev) => ({ ...prev, [field]: e.target.value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Client-side validation for edit fields
  const validateEditFields = () => {
    const errors = {};
    if (!editFields.email?.trim()) {
      errors.email = "Email is required.";
    } else {
      const atIdx = editFields.email.trim().indexOf("@");
      const dotAfter = editFields.email.trim().indexOf(".", atIdx + 1);
      if (atIdx < 1 || dotAfter < atIdx + 2 || editFields.email.trim().length > 254) {
        errors.email = "Please enter a valid email address.";
      }
    }
    if (!editFields.fullName?.trim()) {
      errors.fullName = "Full name is required.";
    } else if (editFields.fullName.trim().length > 255) {
      errors.fullName = "Full name must be at most 255 characters.";
    }
    if (editFields.phone && editFields.phone.trim().length > 255) {
      errors.phone = "Phone must be at most 255 characters.";
    }
    return errors;
  };

  // Save edits
  const handleSaveEdits = async () => {
    setError("");
    setEditSuccess("");
    setFieldErrors({});

    const errors = validateEditFields();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    try {
      setLoading(true);
      await registrationService.updateRecord({
        token: editToken,
        updates: {
          email: editFields.email.trim(),
          formData: {
            fullName: editFields.fullName.trim(),
            phone: editFields.phone?.trim() || "",
          },
        },
      });
      setEditSuccess("Your registration has been updated successfully.");
    } catch (err) {
      const responseData = err.response?.data;
      const serverFieldErrors = extractFieldErrors(responseData);

      if (serverFieldErrors) {
        setFieldErrors(serverFieldErrors);
        setError("Please fix the errors below.");
      } else {
        const message = responseData?.message || responseData?.error;
        const isSessionError =
          message?.toLowerCase().includes("session") ||
          message?.toLowerCase().includes("expired");
        if (isSessionError) {
          setSessionExpired(true);
          setView("otp");
          setOtpPhase("request");
          setEditToken("");
          setError("Edit session has expired. Please request a new OTP.");
        } else {
          setError(message || "Failed to save changes. Please try again.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Back to registration from OTP/edit view
  const handleBackToRegister = () => {
    setView("register");
    clearInstituteState();
    clearOtpState();
    clearSessionState();
    setError("");
    setEditSuccess("");
    setFieldErrors({});
  };

  // Success screen
  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h5" fontWeight={600} mb={2}>
              Registration Submitted
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              Your registration has been submitted successfully. You will
              receive an email once your request is reviewed by the institute.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setSuccess(false);
                setActiveStep(0);
                setPrn("");
                setLookupMode("prn");
                setLookupEmail("");
                setInstituteId(null);
                setInstituteName("");
                setPreAssigned(null);
                setDepartmentId("");
                setClassId("");
                setFirstName("");
                setLastName("");
                setEmail("");
                setPhone("");
                setDob("");
                setGender("");
                setRollNumber("");
                setParentName("");
                setParentPhone("");
                setParentWhatsapp("");
                setParentEmail("");
                setStreet("");
                setCity("");
                setAddressState("");
                setCountry("India");
                setPinCode("");
                setFormErrors({});
              }}
            >
              Submit Another Registration
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // OTP View — request and verify OTP
  if (view === "otp") {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h5" textAlign="center" fontWeight={600} mb={1}>
            Edit Registration
          </Typography>
          <Typography
            variant="body2"
            textAlign="center"
            color="text.secondary"
            mb={3}
          >
            Verify your identity via email OTP to view and edit your registration.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {editSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {editSuccess}
            </Alert>
          )}
          {sessionExpired && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your edit session has expired. Please request a new OTP to continue editing.
            </Alert>
          )}

          {otpPhase === "request" && (
            <Box>
              <TextField
                fullWidth
                label="PRN (Permanent Registration Number)"
                placeholder="Enter your PRN"
                value={otpPrn}
                onChange={(e) => setOtpPrn(e.target.value.toUpperCase())}
                disabled={loading}
                sx={{ mb: 3 }}
                inputProps={{ maxLength: 50 }}
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleRequestOtp}
                disabled={loading || !otpPrn.trim() || otpCooldown > 0}
                sx={{ py: 1.5, mb: 2 }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: "white" }} />
                    <Typography variant="body2" sx={{ color: "white" }}>
                      Sending OTP...
                    </Typography>
                  </Box>
                ) : otpCooldown > 0 ? (
                  `Resend OTP in ${otpCooldown}s`
                ) : (
                  "Send OTP"
                )}
              </Button>
              <Button
                fullWidth
                variant="text"
                size="small"
                onClick={handleBackToRegister}
                sx={{ mt: 1 }}
              >
                Back to Registration
              </Button>
            </Box>
          )}

          {otpPhase === "verify" && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                An OTP has been sent to your registered email for PRN:{" "}
                <strong>{otpPrn}</strong>. It is valid for 10 minutes.
              </Typography>

              <TextField
                fullWidth
                label="Enter 6-digit OTP"
                placeholder="000000"
                value={otp}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtp(val);
                }}
                disabled={loading}
                sx={{ mb: 3 }}
                inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleVerifyOtp}
                disabled={loading || otp.length !== 6}
                sx={{ py: 1.5, mb: 2 }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: "white" }} />
                    <Typography variant="body2" sx={{ color: "white" }}>
                      Verifying...
                    </Typography>
                  </Box>
                ) : (
                  "Verify OTP"
                )}
              </Button>

              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setOtp("");
                    setError("");
                    setEditSuccess("");
                    setOtpPhase("request");
                  }}
                  disabled={otpCooldown > 0}
                >
                  {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Resend OTP"}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleBackToRegister}
                >
                  Back
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    );
  }

  // Edit View — edit the registration record
  if (view === "edit" && record) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h5" textAlign="center" fontWeight={600} mb={1}>
            Edit Registration
          </Typography>
          <Typography
            variant="body2"
            textAlign="center"
            color="text.secondary"
            mb={3}
          >
            Update your registration details below.
          </Typography>

          {/* Session expiry countdown */}
          <Alert
            severity={timeRemaining != null && timeRemaining < 300 ? "warning" : "info"}
            sx={{ mb: 2 }}
          >
            Edit session expires in: <strong>{formatTime(timeRemaining)}</strong>
          </Alert>

          {/* Institute info banner (shown only after OTP verification) */}
          {instituteName && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Institute: {instituteName}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {editSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {editSuccess}
            </Alert>
          )}

          {/* PRN display (read-only) */}
          <TextField
            fullWidth
            label="PRN"
            value={record.prn || otpPrn}
            disabled
            sx={{ mb: 2 }}
          />

          {/* Editable fields */}
          <TextField
            fullWidth
            label="Full Name *"
            placeholder="Enter your full name"
            value={editFields.fullName}
            onChange={handleEditFieldChange("fullName")}
            disabled={loading}
            error={!!fieldErrors.fullName}
            helperText={fieldErrors.fullName || ""}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 255 }}
          />

          <TextField
            fullWidth
            label="Email *"
            placeholder="Enter your email address"
            type="email"
            value={editFields.email}
            onChange={handleEditFieldChange("email")}
            disabled={loading}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email || ""}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 254 }}
          />

          <TextField
            fullWidth
            label="Phone"
            placeholder="Enter your phone number"
            value={editFields.phone}
            onChange={handleEditFieldChange("phone")}
            disabled={loading}
            error={!!fieldErrors.phone}
            helperText={fieldErrors.phone || ""}
            sx={{ mb: 3 }}
            inputProps={{ maxLength: 255 }}
          />

          {/* Department and Class (read-only display) */}
          {record.department && (
            <TextField
              fullWidth
              label="Department"
              value={record.department?.name || record.departmentId || ""}
              disabled
              sx={{ mb: 2 }}
            />
          )}
          {record.class && (
            <TextField
              fullWidth
              label="Class"
              value={record.class?.name || record.classId || ""}
              disabled
              sx={{ mb: 2 }}
            />
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSaveEdits}
            disabled={loading || !editFields.email?.trim() || !editFields.fullName?.trim()}
            sx={{ py: 1.5, mb: 2 }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} sx={{ color: "white" }} />
                <Typography variant="body2" sx={{ color: "white" }}>
                  Saving...
                </Typography>
              </Box>
            ) : (
              "Save Changes"
            )}
          </Button>

          <Button
            fullWidth
            variant="text"
            size="small"
            onClick={handleBackToRegister}
          >
            Back to Registration
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography variant="h5" textAlign="center" fontWeight={600} mb={1}>
          Student Registration
        </Typography>
        <Typography
          variant="body2"
          textAlign="center"
          color="text.secondary"
          mb={3}
        >
          Register for your institute using your Permanent Registration Number
          (PRN).
        </Typography>

        {/* Stepper */}
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}



        {/* Step 1: PRN or Email Lookup */}
        {activeStep === 0 && (
          <Box>
            {lookupMode === "prn" ? (
              <>
                <TextField
                  fullWidth
                  label="PRN (Permanent Registration Number)"
                  placeholder="Enter your PRN"
                  value={prn}
                  onChange={(e) => setPrn(e.target.value.toUpperCase())}
                  disabled={loading}
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 50 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handlePrnLookup}
                  disabled={loading || !prn.trim()}
                  sx={{ py: 1.5, mb: 2 }}
                >
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: "white" }} />
                      <Typography variant="body2" sx={{ color: "white" }}>
                        Looking up...
                      </Typography>
                    </Box>
                  ) : (
                    "Look Up"
                  )}
                </Button>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ textAlign: "center", cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => {
                    setLookupMode("email");
                    setError("");
                  }}
                >
                  Look up by email instead
                </Typography>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Email Address"
                  placeholder="Enter the email used during registration"
                  type="email"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  disabled={loading}
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 254 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleEmailLookup}
                  disabled={loading || !lookupEmail.trim()}
                  sx={{ py: 1.5, mb: 2 }}
                >
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: "white" }} />
                      <Typography variant="body2" sx={{ color: "white" }}>
                        Looking up...
                      </Typography>
                    </Box>
                  ) : (
                    "Look Up"
                  )}
                </Button>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ textAlign: "center", cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => {
                    setLookupMode("prn");
                    setError("");
                  }}
                >
                  Look up by PRN instead
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Step 2: Department & Class */}
        {activeStep === 1 && (
          <Box>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="department-label">Department</InputLabel>
              <Select
                labelId="department-label"
                value={departmentId}
                label="Department"
                onChange={handleDepartmentChange}
                disabled={loading}
              >
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="class-label">Class</InputLabel>
              <Select
                labelId="class-label"
                value={classId}
                label="Class"
                onChange={handleClassChange}
                disabled={!departmentId || loading}
              >
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.section ? `${cls.name} - ${cls.section}` : cls.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  setActiveStep(0);
                  setError("");
                }}
                sx={{ flex: 1, py: 1.5 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleStep2Next}
                disabled={loading || !departmentId || !classId}
                sx={{ flex: 1, py: 1.5 }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Details & Submit */}
        {activeStep === 2 && (
          <Box>
            {/* Show pre-assigned info when department/class were auto-configured */}
            {preAssigned && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Department and class have been pre-assigned by your institute.
              </Alert>
            )}

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Personal Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name *"
                  placeholder="Enter your first name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={loading}
                  error={!!formErrors.firstName}
                  helperText={formErrors.firstName || ""}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  placeholder="Enter your last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={loading}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email *"
                  placeholder="Enter your email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  error={!!formErrors.email}
                  helperText={formErrors.email || ""}
                  inputProps={{ maxLength: 254 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone *"
                  placeholder="Enter your phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={loading}
                  error={!!formErrors.phone}
                  helperText={formErrors.phone || ""}
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  disabled={loading}
                  error={!!formErrors.dob}
                  helperText={formErrors.dob || ""}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="gender-label">Gender</InputLabel>
                  <Select
                    labelId="gender-label"
                    value={gender}
                    label="Gender"
                    onChange={(e) => setGender(e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Roll Number"
                  placeholder="Enter your roll number"
                  value={rollNumber}
                  onChange={(e) => setRollNumber(e.target.value)}
                  disabled={loading}
                  inputProps={{ maxLength: 50 }}
                  helperText={preAssigned?.rollNumber ? "Pre-filled from institute records (you may change it)" : ""}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
              Parent/Guardian Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Parent/Guardian Name"
                  value={parentName}
                  onChange={(e) => setParentName(e.target.value)}
                  disabled={loading}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Parent Phone"
                  placeholder="+919876543210"
                  value={parentPhone}
                  onChange={(e) => setParentPhone(e.target.value)}
                  disabled={loading}
                  error={!!formErrors.parentPhone}
                  helperText={formErrors.parentPhone || ""}
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Parent WhatsApp"
                  placeholder="+919876543210"
                  value={parentWhatsapp}
                  onChange={(e) => setParentWhatsapp(e.target.value)}
                  disabled={loading}
                  error={!!formErrors.parentWhatsapp}
                  helperText={formErrors.parentWhatsapp || "Must start with + followed by country code"}
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Parent Email"
                  placeholder="parent@example.com"
                  type="email"
                  value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  disabled={loading}
                  error={!!formErrors.parentEmail}
                  helperText={formErrors.parentEmail || ""}
                  inputProps={{ maxLength: 254 }}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
              Address Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  disabled={loading}
                  inputProps={{ maxLength: 200 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  disabled={loading}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={addressState}
                    label="State"
                    onChange={(e) => setAddressState(e.target.value)}
                    disabled={loading}
                  >
                    {INDIAN_STATES.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={country}
                    label="Country"
                    onChange={(e) => setCountry(e.target.value)}
                    disabled={loading}
                  >
                    {COUNTRIES.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pin Code"
                  value={pinCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setPinCode(val);
                  }}
                  disabled={loading}
                  error={!!formErrors.pinCode}
                  helperText={formErrors.pinCode || "6-digit numeric code"}
                  inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  // If pre-assigned, go back to Step 1 (skip Step 2)
                  setActiveStep(preAssigned ? 0 : 1);
                  setError("");
                }}
                sx={{ flex: 1, py: 1.5 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={loading || !email.trim() || !firstName.trim() || !phone.trim()}
                sx={{ flex: 1, py: 1.5 }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: "white" }} />
                    <Typography variant="body2" sx={{ color: "white" }}>
                      Submitting...
                    </Typography>
                  </Box>
                ) : (
                  "Submit"
                )}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default RegistrationPortal;
